import apiClient from './apiClient';

/** Connection name → REACT_APP_API_URL_analytics_hub */
export const ANALYTICS_HUB_CONNECTION = 'analytics_hub';

/** Analytics Hub API base URL — http://127.0.0.1:8000/ */
export const ANALYTICS_HUB_BASE_URL = 'http://127.0.0.1:8000/';

function resolveAnalyticsHubBaseUrl(): string {
  const fromEnv = (process.env as Record<string, string | undefined>).REACT_APP_API_URL_analytics_hub?.trim();
  return fromEnv || ANALYTICS_HUB_BASE_URL;
}

function buildAnalyticsHubHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenantId = localStorage.getItem('tenant_id');
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  return headers;
}

async function analyticsHubGet<T>(url: string, params: Record<string, string> = {}): Promise<T> {
  const response = await apiClient.get(url, {
    params,
    baseURL: resolveAnalyticsHubBaseUrl(),
    headers: buildAnalyticsHubHeaders(),
  });
  return response.data;
}

async function analyticsHubPost<T>(
  url: string,
  data: unknown,
  params: Record<string, string> = {}
): Promise<T> {
  const response = await apiClient.post(url, data, {
    params,
    baseURL: resolveAnalyticsHubBaseUrl(),
    headers: buildAnalyticsHubHeaders(),
  });
  return response.data;
}

async function analyticsHubPut<T>(
  url: string,
  data: unknown,
  params: Record<string, string> = {}
): Promise<T> {
  const response = await apiClient.put(url, data, {
    params,
    baseURL: resolveAnalyticsHubBaseUrl(),
    headers: buildAnalyticsHubHeaders(),
  });
  return response.data;
}

async function analyticsHubDelete<T>(url: string, params: Record<string, string> = {}): Promise<T> {
  const response = await apiClient.delete(url, {
    params,
    baseURL: resolveAnalyticsHubBaseUrl(),
    headers: buildAnalyticsHubHeaders(),
  });
  return response.data;
}

export const DEFAULT_ANALYTICS_COLLECTION_ID = '123';

export interface EntityUpsertPayload {
  _id?: string;
  tenant_id: string;
  entity_name: string;
  entity_json: Record<string, unknown> | unknown[];
  analytics_collection_id?: string;
  createdby?: string;
  createdtime?: string;
  updated_by?: string;
  updated_time?: string;
}

export interface ReportDefinitionParameter {
  name: string;
  mapping_type: 'custom' | 'inherit_from_claims';
  isrequired?: boolean;
  value: string;
}

export interface ReportDefinitionUpsertPayload {
  _id?: string;
  report_id?: string;
  tenant_id: string;
  report_name: string;
  report_type?: string;
  description?: string;
  icon?: string;
  source_type?: string;
  source_id?: string;
  parameters?: ReportDefinitionParameter[];
  copilot_ids?: string[];
  agent_chat?: Record<string, unknown>;
  jsx?: string;
  css?: string;
  js?: string;
  entities?: string;
  final_report?: Record<string, unknown>;
  report_header?: Record<string, unknown>;
  collection_id?: string;
  created_by?: string;
  created_time?: string;
  updated_by?: string;
  updated_time?: string;
  ispublished?: boolean;
}

export interface AnalyticsHubReportDefinition {
  _id?: string;
  reportid?: string;
  report_id?: string;
  tenant_id?: string;
  report_name: string;
  report_type?: string;
  description?: string;
  icon?: string;
  source_type?: string;
  source_id?: string;
  parameters?: ReportDefinitionParameter[];
  /** @deprecated Legacy API field — prefer `parameters`. */
  roles?: string[];
  /** @deprecated Legacy API field — prefer `parameters`. */
  users?: string[];
  /** @deprecated Legacy API field — prefer `parameters`. */
  denied?: string[];
  /** @deprecated Legacy API field — prefer `parameters`. */
  allowed_departments?: string[];
  copilot_ids?: string[];
  agent_chat?: Record<string, unknown>;
  jsx?: string;
  css?: string;
  js?: string;
  entities?: string;
  final_report?: Record<string, unknown>;
  report_header?: Record<string, unknown>;
  collectionid?: string;
  collection_id?: string;
  analytics_collection_id?: string;
  created_by?: string;
  created_time?: string;
  updated_by?: string;
  updated_time?: string;
  ispublished?: boolean;
}

export interface GetReportDefinitionsResponse {
  success: boolean;
  message: string;
  data: AnalyticsHubReportDefinition[];
}

export interface GetReportDefinitionByIdResponse {
  success: boolean;
  message: string;
  data: AnalyticsHubReportDefinition | AnalyticsHubReportDefinition[];
}

export interface ReportDefinitionEntitiesData {
  _id?: string;
  entities?: string[];
}

export interface GetReportDefinitionEntitiesResponse {
  success: boolean;
  message: string;
  data: ReportDefinitionEntitiesData;
}

export interface UpdateReportDefinitionEntitiesPayload {
  entities: string[];
}

export interface UpdateReportDefinitionEntitiesResponse {
  success: boolean;
  message: string;
  data?: ReportDefinitionEntitiesData;
}

export interface SemanticDataRecord {
  _id: string;
  analytics_id: string;
  entity_name: string;
  data?: Record<string, unknown> | unknown[];
  data_preview?: string;
  item_count?: number;
  submission_datetime: string;
}

export interface SemanticDataCollection {
  collection: string;
  analytics_id: string;
  items: SemanticDataRecord[];
}

export interface GetReportDefinitionSemanticDataResponse {
  success: boolean;
  message: string;
  data: SemanticDataCollection;
}

export interface SemanticDataPagination {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface SemanticDataDetailRecord {
  _id: string;
  analytics_id: string;
  entity_name: string;
  submission_datetime: string;
  data: Record<string, unknown> | unknown[];
  data_kind: 'array' | 'object' | string;
  pagination: SemanticDataPagination;
}

export interface GetReportDefinitionSemanticDataByIdResponse {
  success: boolean;
  message: string;
  data: SemanticDataDetailRecord;
}

export interface DeleteReportDefinitionSemanticDataResponse {
  success: boolean;
  message: string;
  data: {
    collection: string;
    _id: string;
    analytics_id: string;
  };
}

export interface CreateReportDefinitionSemanticDataPayload {
  entityname: string;
  data: Record<string, unknown> | unknown[];
}

export interface CreateReportDefinitionSemanticDataResponse {
  success: boolean;
  message: string;
  data?: SemanticDataRecord | {
    _id?: string;
    analytics_id?: string;
    entity_name?: string;
    data?: Record<string, unknown> | unknown[];
    submission_datetime?: string;
  };
}

export interface UpsertReportDefinitionResponse {
  success?: boolean;
  message?: string;
  _id?: string;
  report_id?: string;
  data?: AnalyticsHubReportDefinition & { _id?: string };
}

export function resolveReportDbIdFromUpsertResponse(
  response: UpsertReportDefinitionResponse,
  fallback?: string
): string | undefined {
  const id =
    response._id ??
    response.data?._id ??
    response.data?.reportid ??
    fallback;
  const trimmed = id?.trim();
  return trimmed || undefined;
}

export interface JsxToTsxPayload {
  css: string;
  js: string;
  jsx: string;
}

export interface JsxToTsxConversionData {
  tsx: string;
  entities?: string;
}

export interface JsxToTsxResponse {
  success: boolean;
  message: string;
  /** @deprecated Prefer `data.tsx` */
  tsx?: string;
  data?: JsxToTsxConversionData;
}

export function resolveJsxToTsxConversion(
  response: JsxToTsxResponse
): { tsx: string; entities?: string } | null {
  const tsx = response.data?.tsx ?? response.tsx;
  if (!response.success || !tsx?.trim()) return null;

  const entities = response.data?.entities?.trim();
  return { tsx, entities: entities || undefined };
}

export interface AnalyticsHubEntity {
  entityid: string;
  name: string;
  json: Record<string, unknown>;
}

export interface GetEntitiesResponse {
  success: boolean;
  message: string;
  data: AnalyticsHubEntity[];
}

export interface AnalyticsHubCollection {
  collectionid: string;
  collection_name: string;
  description: string;
}

export interface GetAnalyticsHubCollectionsResponse {
  success: boolean;
  message: string;
  data: AnalyticsHubCollection[];
}

export interface AnalyticsHubCollectionUpsertPayload {
  _id?: string;
  collection_name: string;
  description?: string;
}

export interface AnalyticsHubChatAgent {
  id?: string;
  agent_id?: string;
  chat_agent_id?: string;
  agent_title?: string;
  title?: string;
  name?: string;
  description?: string;
  agent_type?: string;
  chat_type?: string;
}

export interface GetChatAgentsResponse {
  success: boolean;
  message: string;
  data: AnalyticsHubChatAgent[];
}

export function mapAnalyticsHubChatAgentOption(
  agent: AnalyticsHubChatAgent
): { label: string; value: string } | null {
  const value = agent.id ?? agent.chat_agent_id ?? agent.agent_id ?? '';
  if (!value) return null;

  const label =
    agent.agent_title ??
    agent.title ??
    agent.name ??
    value;

  return { label, value };
}

export function mapAnalyticsHubCollection(
  api: AnalyticsHubCollection
): { id: string; name: string; description?: string } {
  return {
    id: api.collectionid,
    name: api.collection_name,
    description: api.description || undefined,
  };
}

export function mapAnalyticsHubEntity(api: AnalyticsHubEntity): {
  id: string;
  name: string;
  data: Record<string, unknown>;
} {
  return {
    id: api.entityid,
    name: api.name,
    data: api.json ?? {},
  };
}

export function buildReportDefinitionUpsertPayload(input: {
  reportDbId?: string;
  reportName: string;
  collectionId: string;
  settings: {
    reportId: string;
    reportType: string;
    description: string;
    icon: string;
    sourceType: string;
    workflowId: string;
    dataModelId: string;
    parameters: Array<{
      name: string;
      mapping_type: 'custom' | 'inherit_from_claims';
      isrequired: boolean;
      value: string;
    }>;
    copilotIds: string[];
    reportHeader: Record<string, unknown>;
    ispublished: boolean;
  };
  parts: { html: string; css: string; js: string };
  isCreate?: boolean;
  finalReportTsx?: string;
  entities?: string;
}): ReportDefinitionUpsertPayload {
  const tenantId = localStorage.getItem('tenant_id') ?? '';
  const userId = localStorage.getItem('user_uid') ?? undefined;
  const now = new Date().toISOString();
  const combinedHtml = `${input.parts.html}`.trim();

  return {
    _id: input.reportDbId,
    report_id: input.settings.reportId,
    tenant_id: tenantId,
    report_name: input.reportName,
    report_type: input.settings.reportType,
    description: input.settings.description,
    icon: input.settings.icon,
    source_type: input.settings.sourceType,
    source_id:
      input.settings.sourceType === 'workflow'
        ? input.settings.workflowId
        : input.settings.dataModelId,
    parameters: input.settings.parameters.map((param) => ({
      name: param.name,
      mapping_type: param.mapping_type,
      isrequired: param.isrequired,
      value: param.value,
    })),
    copilot_ids: input.settings.copilotIds,
    jsx: input.parts.html,
    css: input.parts.css,
    js: input.parts.js,
    entities: input.entities,
    report_header: input.settings.reportHeader,
    collection_id: input.collectionId,
    final_report: input.finalReportTsx
      ? { tsx: input.finalReportTsx }
      : { html: combinedHtml },
    created_by: input.isCreate ? userId : undefined,
    created_time: input.isCreate ? now : undefined,
    updated_by: userId,
    updated_time: now,
    ispublished: input.settings.ispublished,
  };
}

export const analyticsHubService = {
  getEntities: (collectionId: string = DEFAULT_ANALYTICS_COLLECTION_ID) =>
    analyticsHubGet<GetEntitiesResponse>('/api/v1/entities', { collection: collectionId }),

  upsertEntity: (payload: EntityUpsertPayload) =>
    analyticsHubPost('/api/v1/entities/upsert', {
      ...payload,
      analytics_collection_id:
        payload.analytics_collection_id ?? DEFAULT_ANALYTICS_COLLECTION_ID,
    }),

  upsertReportDefinition: (payload: ReportDefinitionUpsertPayload) =>
    analyticsHubPost<UpsertReportDefinitionResponse>('/api/v1/report-definitions/upsert', payload),

  getReportDefinitions: (collectionId: string) =>
    analyticsHubGet<GetReportDefinitionsResponse>('/api/v1/report-definitions', {
      collectionid: collectionId,
    }),

  getReportDefinitionById: (id: string) =>
    analyticsHubGet<GetReportDefinitionByIdResponse>(`/api/v1/report-definitions/${id}`),

  getReportDefinitionEntities: (reportDbId: string) =>
    analyticsHubGet<GetReportDefinitionEntitiesResponse>(
      `/api/v1/report-definitions/${reportDbId}/entities`
    ),

  getReportDefinitionSemanticData: (
    analyticsId: string,
    options?: { include_data?: boolean }
  ) =>
    analyticsHubGet<GetReportDefinitionSemanticDataResponse>(
      `/api/v1/report-definitions/${analyticsId}/semantic-data`,
      {
        include_data: String(options?.include_data ?? true),
      }
    ),

  getReportDefinitionSemanticDataById: (
    analyticsId: string,
    recordId: string,
    options?: { page?: number; page_size?: number }
  ) =>
    analyticsHubGet<GetReportDefinitionSemanticDataByIdResponse>(
      `/api/v1/report-definitions/${analyticsId}/semantic-data/${recordId}`,
      {
        page: String(options?.page ?? 1),
        page_size: String(options?.page_size ?? 50),
      }
    ),

  deleteReportDefinitionSemanticData: (analyticsId: string, recordId: string) =>
    analyticsHubDelete<DeleteReportDefinitionSemanticDataResponse>(
      `/api/v1/report-definitions/${analyticsId}/semantic-data/${recordId}`
    ),

  createReportDefinitionSemanticData: (
    analyticsId: string,
    payload: CreateReportDefinitionSemanticDataPayload
  ) =>
    analyticsHubPost<CreateReportDefinitionSemanticDataResponse>(
      `/api/v1/report-definitions/${analyticsId}/semantic-data`,
      payload
    ),

  updateReportDefinitionEntities: (
    reportDbId: string,
    payload: UpdateReportDefinitionEntitiesPayload
  ) =>
    analyticsHubPut<UpdateReportDefinitionEntitiesResponse>(
      `/api/v1/report-definitions/${reportDbId}/entities`,
      payload
    ),

  convertJsxToTsx: (reportDbId: string, payload: JsxToTsxPayload) =>
    analyticsHubPost<JsxToTsxResponse>(
      '/api/v1/convert/jsx-to-tsx',
      payload,
      { report_id: reportDbId.trim() }
    ),

  getAnalyticsHubCollections: () =>
    analyticsHubGet<GetAnalyticsHubCollectionsResponse>('/api/v1/analytics-hub-collections'),

  getChatAgents: () =>
    analyticsHubGet<GetChatAgentsResponse>('/api/v1/chat-agents'),

  upsertAnalyticsHubCollection: (payload: AnalyticsHubCollectionUpsertPayload) =>
    analyticsHubPost('/api/v1/analytics-hub-collections/upsert', payload),
};
